import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { Lock, Mail } from 'lucide-react';
import { login, getRoles, registerForEvent } from '../../utils/authUtils';
import { ADMIN_ROLE } from '../../utils/staffRoles';
import { Dialog } from '../admin/Dialog';
import { profileReturnPath } from '../../utils/profileReturn';
import axios from 'axios';


const PageContainer = styled.div`
  min-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  padding: 12px;
`;

const LoginCard = styled.div`
  max-width: 400px;
  width: 100%;
  background-color: white;
  padding: 2rem;
  border-radius: 0px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const Title = styled.h2`
  margin-top: 1.5rem;
  text-align: center;
  font-size: 1.875rem;
  font-weight: 800;
  color: #1f2937;
`;

const Subtitle = styled.p`
  margin-top: 0.5rem;
  text-align: center;
  font-size: 0.875rem;
  color: #4b5563;
`;

const Form = styled.form`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Input = styled.input`
  appearance: none;
  border: 1px solid #d1d5db;
  border-radius: 0px;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  width: 100%;
  color: #1f2937;

  &:focus {
    outline: none;
    border-color: #47ABDB;
    box-shadow: 0 0 0 3px rgba(71, 171, 219, 0.15);
  }
`;

const ErrorMessage = styled.div`
  background-color: #fee2e2;
  border: 1px solid #f87171;
  color: #b91c1c;
  padding: 0.75rem;
  border-radius: 0px;
  font-size: 0.875rem;
`;

const Button = styled.button`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0px;
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
  background-color: #47ABDB;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #0056b3;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(71, 171, 219, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ForgotPasswordLink = styled.a`
  font-size: 0.875rem;
  color: #47ABDB;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const SuccessMessage = styled.div`
  background-color: #d1fae5;
  border: 1px solid #34d399;
  color: #065f46;
  padding: 0.75rem;
  border-radius: 0px;
  font-size: 0.875rem;
`;

const SignUpPrompt = styled.p`
  margin-top: 1rem;
  text-align: center;
  font-size: 0.875rem;
  color: #4b5563;
`;

const SignUpLink = styled(Link)`
  font-weight: 500;
  color: #47ABDB;

  &:hover {
    text-decoration: underline;
  }
`;

// Redirect target when the caller doesn't pin one via `redirectTo` (the
// /admin/login page still passes an explicit redirectTo and keeps its exact
// prior behavior). Used by the unified navbar login, which has no fixed
// destination - it depends on who just logged in. An admin lands on the admin
// dashboard; a team member and everyone else, program editors included, land
// on the universal profile page (D7). The navbar menu holds the way into the
// admin area. `roles` is the full set GET /api/authenticate answers.
const defaultRedirectFor = (roles) => ((roles || []).includes(ADMIN_ROLE) ? '/admin/dashboard' : '/profile');

/**
 * Shared login form for admin, speaker, and attendee portals alike. All
 * roles authenticate against the same /api/authenticate endpoint; only the
 * copy and the post-login redirect differ. `eventId`, when passed, is an
 * event the visitor was trying to register for before being sent here to
 * log in (see AttendeeLogin) - a successful login completes that
 * registration. `showSignupLink` is attendee-only - admin/speaker accounts
 * aren't self-serve, there's no /signup for them to be pointed at.
 */
const LoginForm = ({ title, subtitle, redirectTo, onLoginSuccess, eventId, showSignupLink = false }) => {
  const intl = useIntl();
  const t = (id, defaultMessage) => intl.formatMessage({ id, defaultMessage });
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('session_expired') === 'true') {
      setError(t('auth.sessionExpired', 'Your session has expired. Please login again.'));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await login(username, password);
      // eventId only ever has a value when this login was reached via the
      // "Register" flow for that specific event (see AttendeeLogin) - never
      // a leftover from something else. The whole outcome - the registration
      // or the reason it did not go through - rides along in navigation
      // state, so the destination (today the profile) shows what happened
      // instead of registering silently.
      const registrationOutcome = await registerForEvent(eventId);
      if (onLoginSuccess) {
        onLoginSuccess();
      }
      const target = redirectTo || defaultRedirectFor(getRoles());
      navigate(target, registrationOutcome ? { state: { registration: registrationOutcome } } : undefined);
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message === 'Session expired. Please login again.'
        ? t('auth.sessionExpired', 'Your session has expired. Please login again.')
        : t('auth.invalidCredentials', 'Invalid credentials or server error. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    // "Forgot password" covers two people: one with an account (the reset
    // mail) and an invited person who has no login yet (the resend, D21,
    // which mails a link that creates the account). POST /api/password/reset
    // decides which one the address is and sends exactly one of the two
    // (PasswordResetController), so one click is one email. The visitor is
    // not told which one they are - the page gives the same answer either way.
    // A refusal (an address that matches nobody included) is not something
    // the visitor can act on, so it keeps the same answer. Only a request
    // that never reached a server (no response at all) is named as a failure.
    let unreachable = false;
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/password/reset`, { email });
    } catch (err) {
      unreachable = !err?.response;
    }
    setIsLoading(false);
    if (unreachable) {
      setError(t('auth.reset.failed', 'Failed to send password reset email. Please try again.'));
    } else {
      setResetEmailSent(true);
    }
  };

  const formContent = (
    <>
      <Form onSubmit={handleLogin}>
        <InputContainer>
          <Input
            id="username"
            name="username"
            type="text"
            required
            placeholder={t('auth.username', 'Username or email')}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </InputContainer>
        <InputContainer>
          <Input
            id="password"
            name="password"
            type="password"
            required
            placeholder={t('auth.password', 'Password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </InputContainer>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <Button type="submit" disabled={isLoading}>
          <Lock size={20} />
          {isLoading ? t('auth.signingIn', 'Signing in…') : t('auth.signIn', 'Sign in')}
        </Button>
      </Form>
      <div style={{ marginTop: '1rem', textAlign: 'center' }}>
        <ForgotPasswordLink onClick={() => setShowForgotPassword(true)}>
          {t('auth.forgotPassword', 'Forgot password?')}
        </ForgotPasswordLink>
      </div>
      {showSignupLink && (
        <SignUpPrompt>
          {t('auth.noAccount', "Don't have an account?")}{' '}
          {/* From a public profile's slides (utils/profileReturn) the signup returns there too; every
              other way here keeps the plain /signup. */}
          <SignUpLink to={profileReturnPath(redirectTo) ? `/signup?redirect=${encodeURIComponent(redirectTo)}` : '/signup'}>{t('auth.signUp', 'Sign up')}</SignUpLink>
        </SignUpPrompt>
      )}
    </>
  );

  return (
    <>
      <PageContainer>
        <LoginCard>
          <Title>{title}</Title>
          <Subtitle>{subtitle}</Subtitle>
          {formContent}
        </LoginCard>
      </PageContainer>

      <Dialog
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        title={t('auth.reset.title', 'Reset Password')}
        description={t('auth.reset.desc', 'Enter your email address to receive a password reset link.')}
      >
        <form onSubmit={handleForgotPassword}>
          <Input
            id="email"
            name="email"
            type="email"
            required
            placeholder={t('auth.reset.email', 'Email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ marginBottom: '1rem' }}
          />
          {resetEmailSent ? (
            <SuccessMessage>
              {t('auth.reset.sent', 'Password reset email sent. Please check your inbox.')}
            </SuccessMessage>
          ) : (
            <Button type="submit" disabled={isLoading}>
              <Mail size={20} />
              {isLoading ? t('auth.reset.sending', 'Sending…') : t('auth.reset.send', 'Send Reset Link')}
            </Button>
          )}
        </form>
      </Dialog>
    </>
  );
};

export default LoginForm;
